import { inject, Injectable } from '@angular/core';
import { catchError, finalize, map, mergeMap, Observable, of, tap } from 'rxjs';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { IOutOfOfficeRequest } from '../../interfaces/out-of-office/IOutOfOfficeRequest';
import { IPaginatedResponse } from '../../interfaces/IPaginatedResponse';
import { OutOfOfficeService } from '../../../out-of-office/out-of-office.service';
import { LoadPendingNotifications } from '../notifications/notifications.actions';
import {
  ClearSelectedOutOfOfficeRequest,
  CreateOutOfOfficeRequest,
  CreateOutOfOfficeRequestComment,
  DeleteOutOfOfficeRequest,
  DeleteOutOfOfficeRequestComment,
  LoadOutOfOfficeRequestById,
  LoadOutOfOfficeRequests,
  UpdateOutOfOfficeRequest,
} from './out-of-office.actions';

export interface IOutOfOfficeStateModel {
  requestsPaginatedResponse: IPaginatedResponse<IOutOfOfficeRequest>;
  selectedRequest: IOutOfOfficeRequest;
  isLoading: boolean;
  isCommentCreating: boolean;
  deletingCommentId: number | null;
}

@State<IOutOfOfficeStateModel>({
  name: 'outOfOfficeState',
  defaults: {
    requestsPaginatedResponse: null,
    selectedRequest: null,
    isLoading: false,
    isCommentCreating: false,
    deletingCommentId: null,
  },
})
@Injectable()
export class OutOfOfficeState {
  private outOfOfficeService = inject(OutOfOfficeService);

  @Selector()
  static requests(state: IOutOfOfficeStateModel): IPaginatedResponse<IOutOfOfficeRequest> {
    return state.requestsPaginatedResponse;
  }

  @Selector()
  static selectedRequest(state: IOutOfOfficeStateModel): IOutOfOfficeRequest {
    return state.selectedRequest;
  }

  @Selector()
  static isLoading(state: IOutOfOfficeStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static isCommentCreating(state: IOutOfOfficeStateModel): boolean {
    return state.isCommentCreating;
  }

  @Selector()
  static deletingCommentId(state: IOutOfOfficeStateModel): number | null {
    return state.deletingCommentId;
  }

  @Action(LoadOutOfOfficeRequests)
  loadRequests(
    ctx: StateContext<IOutOfOfficeStateModel>,
    payload: LoadOutOfOfficeRequests,
  ): Observable<IPaginatedResponse<IOutOfOfficeRequest>> {
    this.setIsLoading(ctx, true);
    return this.outOfOfficeService.getOutOfOfficeRequests(payload.paginationProps, payload.requiresAction).pipe(
      tap(requests => {
        ctx.patchState({
          requestsPaginatedResponse: requests,
        });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadOutOfOfficeRequestById)
  loadRequestById(ctx: StateContext<IOutOfOfficeStateModel>, payload: LoadOutOfOfficeRequestById): Observable<IOutOfOfficeRequest> {
    this.setIsLoading(ctx, true);
    return this.outOfOfficeService.getOutOfOfficeRequestById(payload.requestId).pipe(
      tap((selectedRequest: IOutOfOfficeRequest) => {
        ctx.patchState({
          selectedRequest,
        });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(CreateOutOfOfficeRequest)
  createRequest(ctx: StateContext<IOutOfOfficeStateModel>, action: CreateOutOfOfficeRequest): Observable<IOutOfOfficeRequest> {
    this.setIsLoading(ctx, true);
    return this.outOfOfficeService.createOutOfOfficeRequest(action.request).pipe(
      tap((createdRequest: IOutOfOfficeRequest) => {
        ctx.patchState({
          selectedRequest: createdRequest,
        });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(UpdateOutOfOfficeRequest)
  updateRequest(ctx: StateContext<IOutOfOfficeStateModel>, action: UpdateOutOfOfficeRequest): Observable<IOutOfOfficeRequest> {
    this.setIsLoading(ctx, true);
    return this.outOfOfficeService.updateOutOfOfficeRequest(action.request).pipe(
      tap((updatedRequest: IOutOfOfficeRequest) => {
        ctx.patchState({
          selectedRequest: updatedRequest,
        });
      }),
      // A status change (e.g. resolving an "EnteredInSAP" request) may add or
      // clear a computed notification, so refresh the user's pending
      // notifications. A failure here must not fail the update itself.
      mergeMap((updatedRequest: IOutOfOfficeRequest) =>
        ctx.dispatch(new LoadPendingNotifications()).pipe(
          catchError(() => of(undefined)),
          map(() => updatedRequest),
        ),
      ),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(DeleteOutOfOfficeRequest)
  deleteRequest(ctx: StateContext<IOutOfOfficeStateModel>, action: DeleteOutOfOfficeRequest): Observable<boolean> {
    this.setIsLoading(ctx, true);
    return this.outOfOfficeService.deleteOutOfOfficeRequest(action.requestId).pipe(
      mergeMap((deleted: boolean) =>
        ctx.dispatch(new LoadPendingNotifications()).pipe(
          catchError(() => of(undefined)),
          map(() => deleted),
        ),
      ),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(CreateOutOfOfficeRequestComment)
  createComment(ctx: StateContext<IOutOfOfficeStateModel>, action: CreateOutOfOfficeRequestComment): Observable<IOutOfOfficeRequest> {
    ctx.patchState({ isCommentCreating: true });
    return this.outOfOfficeService.createOutOfOfficeRequestComment(action.requestId, action.comment).pipe(
      tap(selectedRequest => ctx.patchState({ selectedRequest })),
      finalize(() => ctx.patchState({ isCommentCreating: false })),
    );
  }

  @Action(DeleteOutOfOfficeRequestComment)
  deleteComment(ctx: StateContext<IOutOfOfficeStateModel>, action: DeleteOutOfOfficeRequestComment): Observable<IOutOfOfficeRequest> {
    ctx.patchState({ deletingCommentId: action.commentId });
    return this.outOfOfficeService.deleteOutOfOfficeRequestComment(action.commentId).pipe(
      mergeMap(() => this.outOfOfficeService.getOutOfOfficeRequestById(action.requestId)),
      tap(selectedRequest => ctx.patchState({ selectedRequest })),
      finalize(() => ctx.patchState({ deletingCommentId: null })),
    );
  }

  @Action(ClearSelectedOutOfOfficeRequest)
  clearSelectedRequest(ctx: StateContext<IOutOfOfficeStateModel>): void {
    ctx.patchState({
      selectedRequest: null,
      isCommentCreating: false,
      deletingCommentId: null,
    });
  }

  private setIsLoading(ctx: StateContext<IOutOfOfficeStateModel>, isLoading: boolean): void {
    ctx.patchState({ isLoading });
  }
}
